import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  IconButton,
  Typography,
  Box,
  Tooltip,
  Paper,
  Popper,
  ClickAwayListener,
} from "@mui/material";
import {
  PeopleFill,
  PersonCheckFill,
  PersonLinesFill,
  DatabaseFill,
  ClipboardCheckFill,
  JournalBookmarkFill,
  HouseFill,
  ChevronLeft,
  ChevronRight,
  FileEarmarkPlayFill,
  ChevronDown,
  ChevronUp,
  QuestionCircleFill,
  TicketFill,
  BellFill,
} from "react-bootstrap-icons";

const drawerWidth = 268;

const menuStructure = [
  { label: "Dashboard", path: "/dashboard", icon: <HouseFill size={16} /> },
  {
    label: "User Management",
    icon: <PeopleFill size={16} />,
    module: "users",
    children: [
      { label: "All Users", path: "/users", icon: <PeopleFill size={14} /> },
    ],
  },
  {
    label: "Instructor Management",
    icon: <PersonLinesFill size={16} />,
    module: "instructors",
    children: [
      {
        label: "All Instructors",
        path: "/instructors",
        icon: <PersonLinesFill size={14} />,
      },
      {
        label: "Assign Team",
        path: "/assignteam",
        icon: <PersonLinesFill size={14} />,
      },
     
    ],
  },
  {
    label: "Mentor Management",
    icon: <PersonCheckFill size={16} />,
    module: "mentors",
    children: [
      {
        label: "All Mentors",
        path: "/mentors",
        icon: <PersonCheckFill size={14} />,
      },
      {
        label: "Assign Instructor",
        path: "/assigninstructor",
        icon: <PersonCheckFill size={14} />,
      },
       {
        label: "Assign Users",
        path: "/assignUsers",
        icon: <PersonCheckFill size={14} />,
      },
      
    ],
  },
  {
    label: "Data Management",
    icon: <DatabaseFill size={16} />,
    module: "data",
    children: [
      { label: "Location", path: "/data", icon: <DatabaseFill size={14} /> },
      {
        label: "Classes & Workshops",
        path: "/data/classses",
        icon: <JournalBookmarkFill size={14} />,
      },
      {
        label: "Class Attendance",
        path: "/data/attendance",
        icon: <ClipboardCheckFill size={14} />,
      },
      {
        label: "Class Media",
        path: "/data/media",
        icon: <FileEarmarkPlayFill size={14} />,
      },
      {
        label: "Participants Journals",
        path: "/data/pariticipantsjournal",
        icon: <FileEarmarkPlayFill size={14} />,
      },
      {
        label: "Log Management",
        path: "data/logmanagement",
        icon: <FileEarmarkPlayFill size={14} />,
      },
    ],
  },
  {
    label: "Reminders",
    icon: <BellFill size={16} />,
    module: "reminders",
    children: [
      {
        label: "All Reminders",
        path: "/reminders",
        icon: <BellFill size={14} />,
      },
    ],
  },
  {
    label: "Help & Support",
    icon: <QuestionCircleFill size={16} />,
    module: "support",
    children: [
      {
        label: "Support Tickets",
        path: "/support/tickets",
        icon: <TicketFill size={14} />,
      },
    ],
  },
  {
    label: "Static Content",
    icon: <TicketFill size={16} />,
    module: "staticContent",
    children: [
      {
        label: "Static Content Data",
        path: "/static-content/data",
        icon: <TicketFill size={14} />,
      },
    ],
  },
];

// Flyout popup for collapsed parent items
const CollapsedFlyout = ({ item, anchorEl, open, onClose, location }) => {
  return (
    <Popper
      open={open}
      anchorEl={anchorEl}
      placement="right-start"
      style={{ zIndex: 1400 }}
    >
      <ClickAwayListener onClickAway={onClose}>
        <Paper
          elevation={8}
          sx={{
            ml: 1,
            background: "linear-gradient(160deg, #1a237e 0%, #0d47a1 100%)",
            border: "1px solid rgba(255,255,255,0.12)",
            borderRadius: "10px",
            overflow: "hidden",
            minWidth: 200,
            boxShadow: "0 8px 32px rgba(0,0,0,0.4)",
          }}
        >
          {/* Flyout header */}
          <Box
            sx={{
              px: 2,
              py: 1.2,
              borderBottom: "1px solid rgba(255,255,255,0.1)",
              background: "rgba(255,255,255,0.07)",
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: "rgba(255,255,255,0.6)",
                fontWeight: 700,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                fontSize: "0.65rem",
              }}
            >
              {item.label}
            </Typography>
          </Box>

          {/* Flyout links */}
          <List disablePadding sx={{ py: 0.5 }}>
            {item.children.map((sub) => {
              const isActive = location.pathname === sub.path;
              return (
                <ListItemButton
                  key={sub.path}
                  component={Link}
                  to={sub.path}
                  onClick={onClose}
                  sx={{
                    px: 2,
                    py: 0.9,
                    gap: 1.5,
                    position: "relative",
                    backgroundColor: isActive
                      ? "rgba(255,235,59,0.12)"
                      : "transparent",
                    borderLeft: isActive
                      ? "3px solid #ffeb3b"
                      : "3px solid transparent",
                    "&:hover": {
                      backgroundColor: "rgba(255,255,255,0.1)",
                    },
                    transition: "all 0.15s ease",
                  }}
                >
                  <ListItemIcon
                    sx={{
                      color: isActive ? "#ffeb3b" : "rgba(255,255,255,0.6)",
                      minWidth: "auto",
                    }}
                  >
                    {sub.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={sub.label}
                    primaryTypographyProps={{
                      fontSize: "0.82rem",
                      fontWeight: isActive ? 600 : 400,
                      color: isActive ? "#fff" : "rgba(255,255,255,0.85)",
                      fontFamily: "'DM Sans', sans-serif",
                    }}
                  />
                  {isActive && (
                    <Box
                      sx={{
                        width: 5,
                        height: 5,
                        borderRadius: "50%",
                        backgroundColor: "#ffeb3b",
                        flexShrink: 0,
                      }}
                    />
                  )}
                </ListItemButton>
              );
            })}
          </List>
        </Paper>
      </ClickAwayListener>
    </Popper>
  );
};

const Sidebar = ({
  collapsed,
  mobileVisible,
  onMobileClose,
  setSidebarCollapsed,
}) => {
  const location = useLocation();
  const [openDropdown, setOpenDropdown] = useState("");
  const [flyoutAnchor, setFlyoutAnchor] = useState(null);
  const [flyoutModule, setFlyoutModule] = useState(null);

  const toggleDropdown = (module) => {
    setOpenDropdown((prev) => (prev === module ? "" : module));
  };

  const handleCollapsedIconClick = (e, module) => {
    if (flyoutModule === module) {
      setFlyoutAnchor(null);
      setFlyoutModule(null);
    } else {
      setFlyoutAnchor(e.currentTarget);
      setFlyoutModule(module);
    }
  };

  const closeFlyout = () => {
    setFlyoutAnchor(null);
    setFlyoutModule(null);
  };

  useEffect(() => {
    let foundMatch = false;
    menuStructure.forEach((item) => {
      if (
        item.children?.some((sub) => location.pathname.startsWith(sub.path))
      ) {
        setOpenDropdown(item.module);
        foundMatch = true;
      }
    });
    if (!foundMatch) setOpenDropdown("");
  }, [location.pathname]);

  // Close flyout when expanding sidebar
  useEffect(() => {
    if (!collapsed) closeFlyout();
  }, [collapsed]);

  const activeFlyoutItem = menuStructure.find((i) => i.module === flyoutModule);

  return (
    <>
      <Drawer
        variant={mobileVisible ? "temporary" : "permanent"}
        open={mobileVisible || !collapsed}
        onClose={onMobileClose}
        sx={{
          "& .MuiDrawer-paper": {
            width: collapsed ? 72 : drawerWidth,
            background:
              "linear-gradient(175deg, #1a237e 0%, #0d47a1 60%, #0a3a8a 100%)",
            color: "white",
            borderRight: "none",
            transition: "width 0.28s cubic-bezier(0.4,0,0.2,1)",
            overflowX: "hidden",
            zIndex: 100,
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background:
                "radial-gradient(ellipse at 20% 10%, rgba(255,255,255,0.06) 0%, transparent 60%)",
              pointerEvents: "none",
            },
          },
        }}
      >
        {/* ── Logo / Header ── */}
        <Box
          display="flex"
          alignItems="center"
          justifyContent={collapsed ? "center" : "space-between"}
          sx={{
            px: collapsed ? 0 : 2.5,
            py: 2,
            borderBottom: "1px solid rgba(255,255,255,0.1)",
            minHeight: 64,
          }}
        >
          {!collapsed && (
            <Box display="flex" alignItems="center" gap={1.2}>
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: "8px",
                  background: "linear-gradient(135deg, #ffeb3b, #ffc107)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: "0 2px 8px rgba(255,235,59,0.4)",
                  flexShrink: 0,
                }}
              >
                <Typography
                  sx={{
                    fontWeight: 900,
                    fontSize: "0.75rem",
                    color: "#1a237e",
                  }}
                >
                  AP
                </Typography>
              </Box>
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: "1rem",
                  color: "#fff",
                  letterSpacing: "-0.01em",
                  fontFamily: "'DM Sans', sans-serif",
                }}
              >
                Admin Panel
              </Typography>
            </Box>
          )}

          {collapsed && (
            <Box
              sx={{
                width: 34,
                height: 34,
                borderRadius: "9px",
                background: "linear-gradient(135deg, #ffeb3b, #ffc107)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 2px 8px rgba(255,235,59,0.35)",
              }}
            >
              <Typography
                sx={{ fontWeight: 900, fontSize: "0.72rem", color: "#1a237e" }}
              >
                AP
              </Typography>
            </Box>
          )}

          {!collapsed && (
            <IconButton
              onClick={() => setSidebarCollapsed(!collapsed)}
              size="small"
              sx={{
                color: "rgba(255,255,255,0.7)",
                backgroundColor: "rgba(255,255,255,0.08)",
                borderRadius: "8px",
                width: 28,
                height: 28,
                "&:hover": {
                  backgroundColor: "rgba(255,255,255,0.15)",
                  color: "#fff",
                },
              }}
            >
              <ChevronLeft size={14} />
            </IconButton>
          )}
        </Box>

        {/* Expand button when collapsed */}
        {collapsed && (
          <Box display="flex" justifyContent="center" sx={{ pt: 1.5, pb: 0.5 }}>
            <IconButton
              onClick={() => setSidebarCollapsed(false)}
              size="small"
              sx={{
                color: "rgba(255,255,255,0.6)",
                backgroundColor: "rgba(255,255,255,0.07)",
                borderRadius: "8px",
                width: 32,
                height: 32,
                "&:hover": {
                  backgroundColor: "rgba(255,255,255,0.15)",
                  color: "#fff",
                },
              }}
            >
              <ChevronRight size={14} />
            </IconButton>
          </Box>
        )}

        {/* ── Section label ── */}
        {!collapsed && (
          <Typography
            sx={{
              px: 2.5,
              pt: 2,
              pb: 0.5,
              fontSize: "0.62rem",
              fontWeight: 700,
              letterSpacing: "0.1em",
              textTransform: "uppercase",
              color: "rgba(255,255,255,0.35)",
              fontFamily: "'DM Sans', sans-serif",
            }}
          >
            Navigation
          </Typography>
        )}

        {/* ── Menu ── */}
        <List
          sx={{ px: collapsed ? 0.8 : 1.2, pt: collapsed ? 1 : 0.5, pb: 2 }}
        >
          {menuStructure.map((item) => {
            const isDirectlyActive =
              item.path && location.pathname === item.path;
            const hasActiveChild = item.children?.some(
              (sub) => location.pathname === sub.path,
            );

            return (
              <React.Fragment key={item.label}>
                {!item.children ? (
                  /* ── Direct link ── */
                  <Tooltip
                    title={collapsed ? item.label : ""}
                    placement="right"
                    arrow
                  >
                    <ListItemButton
                      component={Link}
                      to={item.path}
                      sx={{
                        borderRadius: "9px",
                        mx: 0,
                        my: 0.3,
                        px: collapsed ? 0 : 1.4,
                        py: 0.85,
                        justifyContent: collapsed ? "center" : "flex-start",
                        backgroundColor: isDirectlyActive
                          ? "rgba(255,235,59,0.14)"
                          : "transparent",
                        borderLeft: !collapsed
                          ? isDirectlyActive
                            ? "3px solid #ffeb3b"
                            : "3px solid transparent"
                          : "none",
                        boxShadow: isDirectlyActive
                          ? "inset 0 0 0 1px rgba(255,235,59,0.15)"
                          : "none",
                        "&:hover": {
                          backgroundColor: "rgba(255,255,255,0.09)",
                        },
                        transition: "all 0.15s ease",
                      }}
                    >
                      <ListItemIcon
                        sx={{
                          color: isDirectlyActive
                            ? "#ffeb3b"
                            : "rgba(255,255,255,0.75)",
                          minWidth: collapsed ? "auto" : 36,
                        }}
                      >
                        {item.icon}
                      </ListItemIcon>
                      {!collapsed && (
                        <ListItemText
                          primary={item.label}
                          primaryTypographyProps={{
                            fontSize: "0.84rem",
                            fontWeight: isDirectlyActive ? 600 : 400,
                            color: isDirectlyActive
                              ? "#fff"
                              : "rgba(255,255,255,0.85)",
                            fontFamily: "'DM Sans', sans-serif",
                          }}
                        />
                      )}
                    </ListItemButton>
                  </Tooltip>
                ) : (
                  <>
                    {/* ── Parent with children ── */}
                    {collapsed ? (
                      /* Collapsed: icon button that opens flyout */
                      <Tooltip title={item.label} placement="right" arrow>
                        <ListItemButton
                          onClick={(e) =>
                            handleCollapsedIconClick(e, item.module)
                          }
                          sx={{
                            borderRadius: "9px",
                            mx: 0,
                            my: 0.3,
                            px: 0,
                            py: 0.85,
                            justifyContent: "center",
                            backgroundColor:
                              flyoutModule === item.module || hasActiveChild
                                ? "rgba(255,235,59,0.14)"
                                : "transparent",
                            boxShadow:
                              flyoutModule === item.module || hasActiveChild
                                ? "inset 0 0 0 1px rgba(255,235,59,0.15)"
                                : "none",
                            "&:hover": {
                              backgroundColor: "rgba(255,255,255,0.09)",
                            },
                            transition: "all 0.15s ease",
                            position: "relative",
                          }}
                        >
                          <ListItemIcon
                            sx={{
                              color:
                                hasActiveChild || flyoutModule === item.module
                                  ? "#ffeb3b"
                                  : "rgba(255,255,255,0.75)",
                              minWidth: "auto",
                            }}
                          >
                            {item.icon}
                          </ListItemIcon>
                          {/* Small dot indicator if has active child */}
                          {hasActiveChild && (
                            <Box
                              sx={{
                                position: "absolute",
                                top: 6,
                                right: 6,
                                width: 5,
                                height: 5,
                                borderRadius: "50%",
                                backgroundColor: "#ffeb3b",
                              }}
                            />
                          )}
                        </ListItemButton>
                      </Tooltip>
                    ) : (
                      /* Expanded: normal toggle */
                      <ListItemButton
                        onClick={() => toggleDropdown(item.module)}
                        sx={{
                          borderRadius: "9px",
                          mx: 0,
                          my: 0.3,
                          px: 1.4,
                          py: 1,
                          backgroundColor: hasActiveChild
                            ? "rgba(255,235,59,0.1)"
                            : "transparent",
                          borderLeft: hasActiveChild
                            ? "3px solid rgba(255,235,59,0.5)"
                            : "3px solid transparent",
                          "&:hover": {
                            backgroundColor: "rgba(255,255,255,0.09)",
                          },
                          transition: "all 0.15s ease",
                        }}
                      >
                        <ListItemIcon
                          sx={{
                            color: hasActiveChild
                              ? "#ffeb3b"
                              : "rgba(255,255,255,0.75)",
                            minWidth: 36,
                          }}
                        >
                          {item.icon}
                        </ListItemIcon>
                        <ListItemText
                          primary={item.label}
                          primaryTypographyProps={{
                            fontSize: "0.84rem",
                            fontWeight: hasActiveChild ? 600 : 400,
                            color: hasActiveChild
                              ? "#fff"
                              : "rgba(255,255,255,0.85)",
                            fontFamily: "'DM Sans', sans-serif",
                          }}
                        />
                        <Box
                          sx={{
                            color: "rgba(255,255,255,0.45)",
                            display: "flex",
                            ml: 0.5,
                          }}
                        >
                          {openDropdown === item.module ? (
                            <ChevronUp size={13} />
                          ) : (
                            <ChevronDown size={13} />
                          )}
                        </Box>
                      </ListItemButton>
                    )}

                    {/* ── Submenu (expanded sidebar only) ── */}
                    {!collapsed && (
                      <Collapse
                        in={openDropdown === item.module}
                        timeout={200}
                        unmountOnExit
                      >
                        <List
                          component="div"
                          disablePadding
                          sx={{ mt: 0.3, mb: 0.5 }}
                        >
                          {item.children.map((sub) => {
                            const isSubActive = location.pathname === sub.path;
                            return (
                              <ListItemButton
                                key={sub.path}
                                component={Link}
                                to={sub.path}
                                sx={{
                                  pl: 4.5,
                                  pr: 1.5,
                                  py: 0.7,
                                  borderRadius: "8px",
                                  mx: 0,
                                  my: 0.15,
                                  backgroundColor: isSubActive
                                    ? "rgba(255,235,59,0.13)"
                                    : "transparent",
                                  borderLeft: isSubActive
                                    ? "3px solid #ffeb3b"
                                    : "3px solid transparent",
                                  "&:hover": {
                                    backgroundColor: "rgba(255,255,255,0.08)",
                                  },
                                  transition: "all 0.15s ease",
                                }}
                              >
                                <ListItemIcon
                                  sx={{
                                    color: isSubActive
                                      ? "#ffeb3b"
                                      : "rgba(255,255,255,0.5)",
                                    minWidth: 30,
                                  }}
                                >
                                  {sub.icon}
                                </ListItemIcon>
                                <ListItemText
                                  primary={sub.label}
                                  primaryTypographyProps={{
                                    fontSize: "0.8rem",
                                    fontWeight: isSubActive ? 600 : 400,
                                    color: isSubActive
                                      ? "#fff"
                                      : "rgba(255,255,255,0.75)",
                                    fontFamily: "'DM Sans', sans-serif",
                                  }}
                                />
                                {isSubActive && (
                                  <Box
                                    sx={{
                                      width: 5,
                                      height: 5,
                                      borderRadius: "50%",
                                      backgroundColor: "#ffeb3b",
                                      flexShrink: 0,
                                    }}
                                  />
                                )}
                              </ListItemButton>
                            );
                          })}
                        </List>
                      </Collapse>
                    )}
                  </>
                )}
              </React.Fragment>
            );
          })}
        </List>
      </Drawer>

      {/* ── Collapsed Flyout Popper ── */}
      {activeFlyoutItem && (
        <CollapsedFlyout
          item={activeFlyoutItem}
          anchorEl={flyoutAnchor}
          open={Boolean(flyoutAnchor)}
          onClose={closeFlyout}
          location={location}
        />
      )}
    </>
  );
};

export default Sidebar;
